import Airtable from 'airtable';

import { pushService } from './pushService';

class AirtableService {
  constructor() {
    this.base = null;
    this.isInitialized = false;
    this.apiKey = null;
    this.baseId = null;
  }

  initialize(apiKey, baseId) {
    try {
      Airtable.configure({
        endpointUrl: 'https://api.airtable.com',
        apiKey: apiKey
      });
      
      this.base = Airtable.base(baseId);
      this.apiKey = apiKey;
      this.baseId = baseId;
      this.isInitialized = true;
      
      localStorage.setItem('airtable_config', JSON.stringify({ apiKey, baseId }));
      
      return true;
    } catch (error) {
      console.error('Airtable initialization failed:', error);
      return false;
    }
  }

  loadSavedConfig() {
    // First try environment variables
    const envApiKey = process.env.REACT_APP_AIRTABLE_API_KEY;
    const envBaseId = process.env.REACT_APP_AIRTABLE_BASE_ID;
    
    console.log('Loading Airtable config:');
    console.log('Environment API Key:', envApiKey ? 'Found' : 'Not found');
    console.log('Environment Base ID:', envBaseId ? 'Found' : 'Not found');
    
    if (envApiKey && envBaseId) {
      console.log('Using environment variables for Airtable config');
      return this.initialize(envApiKey, envBaseId);
    }
    
    // Fall back to localStorage
    const savedConfig = localStorage.getItem('airtable_config');
    console.log('LocalStorage config:', savedConfig ? 'Found' : 'Not found');
    
    if (savedConfig) {
      const { apiKey, baseId } = JSON.parse(savedConfig);
      console.log('Using localStorage for Airtable config');
      return this.initialize(apiKey, baseId);
    }
    
    console.log('No Airtable configuration found');
    return false;
  }

  async setupTablesIfNeeded() {
    if (!this.isInitialized) {
      console.error('Cannot setup tables: Airtable not initialized');
      return false;
    }

    console.log('Setting up Airtable tables...');
    try {
      // Check if tables exist and create them if needed
      console.log('Checking/creating Sessions table...');
      await this.createSessionsTable();

      console.log('Checking/creating Queue table...');
      await this.createQueueTable();

      console.log('Checking/creating Evaluations table...');
      await this.createEvaluationsTable();

      // Add rubric fields to existing tables if missing
      console.log('Checking for schema migrations...');
      await this.migrateSchema();

      console.log('All tables setup completed successfully');
      return true;
    } catch (error) {
      console.error('Error setting up tables:', error);
      console.error('Error details:', error.message);
      return false;
    }
  }

  async checkTableExists(tableName) {
    try {
      await this.base(tableName).select({ maxRecords: 1 }).firstPage();
      return { exists: true, accessible: true };
    } catch (error) {
      if (error.statusCode === 404) {
        return { exists: false, accessible: false, error: 'Table not found' };
      } else if (error.statusCode === 403) {
        return { exists: true, accessible: false, error: 'Permission denied' };
      } else {
        return { exists: false, accessible: false, error: error.message };
      }
    }
  }

  async listAllTables() {
    if (!this.isInitialized) return [];
    
    const tableNames = ['Sessions', 'Queue', 'Evaluations'];
    const results = {};
    
    for (const tableName of tableNames) {
      results[tableName] = await this.checkTableExists(tableName);
    }
    
    return results;
  }

  async createSessionsTable() {
    try {
      // Try to access the table first to see if it exists
      await this.base('Sessions').select({ maxRecords: 1 }).firstPage();
      console.log('Sessions table already exists');
      return { success: true, created: false };
    } catch (error) {
      if (error.statusCode === 404) {
        console.log('Creating Sessions table...');
        try {
          await this.createTableViaAPI('Sessions', [
            { name: 'Session ID', type: 'singleLineText' },
            { name: 'Doctor Name', type: 'singleLineText' },
            { name: 'Access Code', type: 'singleLineText' },
            { name: 'Clinic Code', type: 'singleLineText' },
            { name: 'Start Time', type: 'dateTime', options: { dateFormat: { name: 'iso' }, timeFormat: { name: '24hour' }, timeZone: 'client' } },
            { name: 'End Time', type: 'dateTime', options: { dateFormat: { name: 'iso' }, timeFormat: { name: '24hour' }, timeZone: 'client' } },
            { name: 'Status', type: 'singleSelect', options: { choices: [{ name: 'active' }, { name: 'ended' }] } }
          ]);
          return { success: true, created: true };
        } catch (createError) {
          console.log('Failed to create Sessions table via API:', createError.message);
          return { success: false, created: false, error: createError.message };
        }
      } else {
        throw error;
      }
    }
  }

  async createQueueTable() {
    try {
      await this.base('Queue').select({ maxRecords: 1 }).firstPage();
      console.log('Queue table already exists');
    } catch (error) {
      if (error.statusCode === 404) {
        console.log('Creating Queue table...');
        await this.createTableViaAPI('Queue', [
          { name: 'Session ID', type: 'singleLineText' },
          { name: 'Student Name', type: 'singleLineText' },
          { name: 'Clinic Code', type: 'singleLineText' },
          { name: 'Join Time', type: 'dateTime', options: { dateFormat: { name: 'iso' }, timeFormat: { name: '24hour' }, timeZone: 'client' } },
          { name: 'Response Time', type: 'dateTime', options: { dateFormat: { name: 'iso' }, timeFormat: { name: '24hour' }, timeZone: 'client' } },
          { name: 'Status', type: 'singleSelect', options: { choices: [{ name: 'waiting' }, { name: 'in_progress' }, { name: 'completed' }, { name: 'removed' }] } },
          { name: 'Position', type: 'number', options: { precision: 0 } },
          { name: 'Is Emergency', type: 'checkbox' },
          { name: 'Queue Reason', type: 'singleLineText' },
          { name: 'Operatory Number', type: 'singleLineText' },
          { name: 'Year Level', type: 'singleLineText' }
        ]);
      } else {
        throw error;
      }
    }
  }

  async createEvaluationsTable() {
    try {
      await this.base('Evaluations').select({ maxRecords: 1 }).firstPage();
      console.log('Evaluations table already exists');
    } catch (error) {
      if (error.statusCode === 404) {
        console.log('Creating Evaluations table...');
        await this.createTableViaAPI('Evaluations', [
          { name: 'Session ID', type: 'singleLineText' },
          { name: 'Student Name', type: 'singleLineText' },
          { name: 'Queue Record ID', type: 'singleLineText' },
          { name: 'Year Level', type: 'singleLineText' },
          { name: 'Professionalism Score', type: 'number', options: { precision: 0 } },
          { name: 'Problem Solving Score', type: 'number', options: { precision: 0 } },
          { name: 'Clinical Skill Score', type: 'number', options: { precision: 0 } },
          { name: 'Was Prepared', type: 'checkbox' },
          { name: 'Missed Findings', type: 'checkbox' },
          { name: 'Was Professional', type: 'checkbox' },
          { name: 'Requeue Student', type: 'checkbox' },
          { name: 'Evaluation Skipped', type: 'checkbox' },
          { name: 'Notes', type: 'multilineText' },
          { name: 'Evaluation Time', type: 'dateTime', options: { dateFormat: { name: 'iso' }, timeFormat: { name: '24hour' }, timeZone: 'client' } },
          { name: 'Response Time Minutes', type: 'number', options: { precision: 0 } }
        ]);
      } else {
        throw error;
      }
    }
  }

  async createTableViaAPI(tableName, fields) {
    const url = `https://api.airtable.com/v0/meta/bases/${this.baseId}/tables`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: tableName,
        fields: fields
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to create table ${tableName}: ${errorData.error?.message || response.statusText}`);
    }

    const data = await response.json();
    console.log(`Successfully created table: ${tableName}`);
    return data;
  }

  async migrateSchema() {
    try {
      const url = `https://api.airtable.com/v0/meta/bases/${this.baseId}/tables`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${this.apiKey}` }
      });
      if (!response.ok) {
        console.log('Could not fetch table metadata for migration');
        return;
      }

      const data = await response.json();
      const tableMap = {};
      data.tables.forEach(t => {
        tableMap[t.name] = { id: t.id, fields: t.fields.map(f => f.name) };
      });

      // Add Year Level to Queue table if missing
      if (tableMap['Queue'] && !tableMap['Queue'].fields.includes('Year Level')) {
        console.log('Adding Year Level field to Queue table...');
        await this.addFieldToTable(tableMap['Queue'].id, { name: 'Year Level', type: 'singleLineText' });
      }

      // Add rubric score fields to Evaluations table if missing
      if (tableMap['Evaluations']) {
        const evalFields = tableMap['Evaluations'].fields;
        const newFields = [
          { name: 'Year Level', type: 'singleLineText' },
          { name: 'Professionalism Score', type: 'number', options: { precision: 0 } },
          { name: 'Problem Solving Score', type: 'number', options: { precision: 0 } },
          { name: 'Clinical Skill Score', type: 'number', options: { precision: 0 } },
          { name: 'Evaluation Skipped', type: 'checkbox' }
        ];
        for (const field of newFields) {
          if (!evalFields.includes(field.name)) {
            console.log(`Adding ${field.name} field to Evaluations table...`);
            await this.addFieldToTable(tableMap['Evaluations'].id, field);
          }
        }
      }

      console.log('Schema migration check complete');
    } catch (error) {
      console.error('Schema migration error (non-fatal):', error);
    }
  }

  async addFieldToTable(tableId, fieldConfig) {
    const url = `https://api.airtable.com/v0/meta/bases/${this.baseId}/tables/${tableId}/fields`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(fieldConfig)
    });
    if (!response.ok) {
      const errorData = await response.json();
      console.log(`Field ${fieldConfig.name} may already exist:`, errorData);
    }
  }

  async createSession(sessionData) {
    if (!this.isInitialized) {
      console.error('Airtable not initialized when trying to create session');
      return null;
    }
    
    console.log('Creating session with data:', sessionData);
    
    try {
      // Store time in UTC ISO format for consistency
      const startTime = new Date(sessionData.startTime);
      const formattedStartTime = startTime.toISOString();
      
      console.log('Original start time:', sessionData.startTime);
      console.log('Formatted start time (UTC ISO):', formattedStartTime);
      
      const records = await this.base('Sessions').create([{
        fields: {
          'Session ID': sessionData.sessionId,
          'Doctor Name': sessionData.doctorName,
          'Access Code': sessionData.accessCode,
          'Clinic Code': sessionData.clinicCode,
          'Start Time': formattedStartTime,
          'Status': 'active'
        }
      }]);
      
      console.log('Session created successfully:', records[0]);
      return records[0];
    } catch (error) {
      console.error('Error creating session:', error);
      console.error('Error details:', error.message, error.statusCode);
      console.error('Full error object:', error);
      return null;
    }
  }

  async endSession(sessionId, endTime) {
    if (!this.isInitialized) return null;
    
    try {
      // Store time in UTC ISO format for consistency
      const endTimeObj = new Date(endTime);
      const formattedEndTime = endTimeObj.toISOString();
      
      console.log('Ending session with UTC ISO format:', formattedEndTime);
      
      const sessions = await this.base('Sessions').select({
        filterByFormula: `{Session ID} = '${sessionId}'`
      }).firstPage();
      
      if (sessions.length > 0) {
        const updatedRecord = await this.base('Sessions').update(sessions[0].id, {
          'End Time': formattedEndTime,
          'Status': 'ended'
        });
        console.log('Session ended successfully');
        return updatedRecord;
      }
      return null;
    } catch (error) {
      console.error('Error ending session:', error);
      return null;
    }
  }

  async addToQueue(queueData) {
    if (!this.isInitialized) return null;
    
    try {
      // Store time in UTC ISO format for consistency
      const joinTime = new Date(queueData.joinTime);
      const formattedJoinTime = joinTime.toISOString();
      
      console.log('Adding to queue with UTC ISO join time:', formattedJoinTime);
      
      const fields = {
        'Session ID': queueData.sessionId,
        'Student Name': queueData.studentName,
        'Clinic Code': queueData.clinicCode,
        'Join Time': formattedJoinTime,
        'Status': 'waiting',
        'Position': queueData.position || 1,
        'Is Emergency': queueData.isEmergency || false
      };

      if (queueData.queueReason) {
        fields['Queue Reason'] = queueData.queueReason;
      }
      if (queueData.operatoryNumber) {
        fields['Operatory Number'] = queueData.operatoryNumber;
      }
      if (queueData.yearLevel) {
        fields['Year Level'] = queueData.yearLevel;
      }

      const records = await this.base('Queue').create([{ fields }]);

      console.log('Successfully added to queue:', records[0]);

      // Wake the push hub so Dr. Grant's devices get a lock-screen
      // notification even when the dashboard is closed. Fire-and-forget:
      // a hub outage never blocks the queue join.
      pushService.notifyQueueJoin(records[0]?.id);

      return records[0];
      
    } catch (error) {
      console.error('Error adding to queue:', error);
      console.error('Error details:', error.message, error.statusCode);
      return null;
    }
  }

  async getQueueForSession(sessionId) {
    if (!this.isInitialized) return [];
    
    try {
      console.log('🔍 Getting queue records for session:', sessionId);

      // Show students from this session that are not completed or removed
      const allRecords = await this.base('Queue').select({
        filterByFormula: `AND({Session ID} = '${sessionId}', {Status} != 'completed', {Status} != 'removed')`
      }).all();
      
      // Sort with emergencies first, then by manual position, then by join time
      allRecords.sort((a, b) => {
        const aIsEmergency = a.fields['Is Emergency'] || false;
        const bIsEmergency = b.fields['Is Emergency'] || false;
        
        // Emergency students come first
        if (aIsEmergency && !bIsEmergency) return -1;
        if (!aIsEmergency && bIsEmergency) return 1;
        
        // Within same priority level, sort by Position (for manual reordering), then join time
        const aPosition = a.fields['Position'] || 999999;
        const bPosition = b.fields['Position'] || 999999;
        
        if (aPosition !== bPosition) {
          return aPosition - bPosition;
        }
        
        // If positions are the same, sort by join time
        return new Date(a.fields['Join Time']) - new Date(b.fields['Join Time']);
      });
      
      console.log('📊 Total active queue records:', allRecords.length);
      
      if (allRecords.length > 0) {
        console.log('📋 All queue records:');
        allRecords.forEach((record, index) => {
          console.log(`  ${index + 1}. Session ID: "${record.fields['Session ID']}", Status: "${record.fields['Status']}", Student: "${record.fields['Student Name']}"`);
        });
      }
      
      return allRecords;
    } catch (error) {
      console.error('Error fetching queue:', error);
      console.error('Error details:', error.message, error.statusCode);
      return [];
    }
  }

  async updateQueueStatus(recordId, status, responseTime = null) {
    if (!this.isInitialized) return null;
    
    try {
      const updateFields = { 'Status': status };
      if (responseTime) {
        // Store response time in UTC ISO format for consistency
        const formattedResponseTime = new Date(responseTime).toISOString();
        updateFields['Response Time'] = formattedResponseTime;
        console.log('Updating queue with UTC ISO response time:', formattedResponseTime);
      }
      
      const updatedRecord = await this.base('Queue').update(recordId, updateFields);
      return updatedRecord;
    } catch (error) {
      console.error('Error updating queue status:', error);
      return null;
    }
  }

  async createEvaluation(evaluationData) {
    if (!this.isInitialized) return null;
    
    try {
      // Store evaluation time in UTC ISO format for consistency
      const evaluationTime = new Date(evaluationData.evaluationTime);
      const formattedEvaluationTime = evaluationTime.toISOString();
      
      console.log('Creating evaluation with UTC ISO time:', formattedEvaluationTime);
      
      const fields = {
        'Session ID': evaluationData.sessionId,
        'Student Name': evaluationData.studentName,
        'Queue Record ID': evaluationData.queueRecordId,
        'Requeue Student': evaluationData.requeueStudent,
        'Notes': evaluationData.notes,
        'Evaluation Time': formattedEvaluationTime,
        'Response Time Minutes': evaluationData.responseTimeMinutes
      };

      if (evaluationData.evaluationSkipped) {
        fields['Evaluation Skipped'] = true;
      }

      // Rubric score fields
      if (evaluationData.yearLevel) {
        fields['Year Level'] = evaluationData.yearLevel;
      }
      if (evaluationData.professionalismScore != null) {
        fields['Professionalism Score'] = evaluationData.professionalismScore;
      }
      if (evaluationData.problemSolvingScore != null) {
        fields['Problem Solving Score'] = evaluationData.problemSolvingScore;
      }
      if (evaluationData.clinicalSkillScore != null) {
        fields['Clinical Skill Score'] = evaluationData.clinicalSkillScore;
      }

      const records = await this.base('Evaluations').create([{ fields }]);
      
      console.log('Evaluation created successfully:', records[0]);
      return records[0];
    } catch (error) {
      console.error('Error creating evaluation:', error);
      console.error('Error details:', error.message, error.statusCode);
      return null;
    }
  }

  async getSessionEvaluations(sessionId) {
    if (!this.isInitialized) return [];
    
    try {
      const records = await this.base('Evaluations').select({
        filterByFormula: `{Session ID} = '${sessionId}'`,
        sort: [{ field: 'Evaluation Time', direction: 'asc' }]
      }).all();
      
      return records;
    } catch (error) {
      console.error('Error fetching evaluations:', error);
      return [];
    }
  }

  async checkStudentInQueue(sessionId, studentName) {
    if (!this.isInitialized) return false;
    
    try {
      const records = await this.base('Queue').select({
        filterByFormula: `AND({Session ID} = '${sessionId}', {Student Name} = '${studentName}', {Status} != 'completed', {Status} != 'removed')`
      }).firstPage();
      
      return records.length > 0;
    } catch (error) {
      console.error('Error checking student in queue:', error);
      return false;
    }
  }

  async validateSession(accessCode, clinicCode) {
    if (!this.isInitialized) return null;
    
    try {
      const records = await this.base('Sessions').select({
        filterByFormula: `AND({Access Code} = '${accessCode}', {Clinic Code} = '${clinicCode}', {Status} = 'active')`
      }).firstPage();
      
      return records.length > 0 ? records[0] : null;
    } catch (error) {
      console.error('Error validating session:', error);
      return null;
    }
  }

  async reorderQueue(sessionId, newOrder) {
    if (!this.isInitialized) return false;
    
    try {
      // Update the Position field to reflect new order
      // Emergency students should maintain their emergency status
      const updates = newOrder.map((item, index) => ({
        id: item.id,
        fields: { 'Position': index + 1 }
      }));
      
      console.log('🔄 Reordering queue with new positions:', updates.map(u => `${u.id}: pos ${u.fields.Position}`));
      
      await this.base('Queue').update(updates);
      console.log('✅ Queue reordering successful');
      return true;
    } catch (error) {
      console.error('Error reordering queue:', error);
      console.error('Error details:', error.message, error.statusCode);
      return false;
    }
  }

  async removeFromQueue(recordId) {
    if (!this.isInitialized) return false;
    
    try {
      await this.base('Queue').update(recordId, {
        'Status': 'removed'
      });
      return true;
    } catch (error) {
      console.error('Error removing from queue:', error);
      return false;
    }
  }

  async getAllSessions() {
    if (!this.isInitialized) return [];

    try {
      const records = await this.base('Sessions').select({
        sort: [{ field: 'Start Time', direction: 'desc' }],
        maxRecords: 10
      }).all();

      return records;
    } catch (error) {
      console.error('Error fetching sessions:', error);
      return [];
    }
  }

  async getSessionHistory(limit = 50) {
    if (!this.isInitialized) return [];

    try {
      const records = await this.base('Sessions').select({
        sort: [{ field: 'Start Time', direction: 'desc' }],
        maxRecords: limit
      }).all();

      return records;
    } catch (error) {
      console.error('Error fetching session history:', error);
      return [];
    }
  }

  async getSessionBySessionId(sessionId) {
    if (!this.isInitialized) return null;

    try {
      const records = await this.base('Sessions').select({
        filterByFormula: `{Session ID} = '${sessionId}'`
      }).firstPage();

      return records.length > 0 ? records[0] : null;
    } catch (error) {
      console.error('Error fetching session by id:', error);
      return null;
    }
  }

  async getActiveSessions() {
    if (!this.isInitialized) return [];
    
    try {
      const records = await this.base('Sessions').select({
        filterByFormula: `{Status} = 'active'`,
        sort: [{ field: 'Start Time', direction: 'desc' }]
      }).all();
      
      return records;
    } catch (error) {
      console.error('Error fetching active sessions:', error);
      return [];
    }
  }
}

export const airtableService = new AirtableService();